import { matchRequestFacilityLoadingState } from "@/atoms/match/matchRequestLoadingState";
import { FacilityMatchRequestProps } from "@/containers/MatchContainer/type/requestType";
import { useDialog } from "@/hooks/useDialog";
import { Box, Input } from "@chakra-ui/react";
import emailjs from "@emailjs/browser";
import {
  Checkbox,
  colors,
  customEvent,
  Dropdown,
  Field,
  FUNERAL_SIDO,
  FUNERAL_SIDO_LIST,
  FUNERAL_SIGUNGU,
  HStack,
  LargeButton,
  LARGEL_BUTTON_TYPE,
  Spacing,
  Spinner,
  useResponsive,
  VStack,
} from "goi_common";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useRecoilState } from "recoil";

const SIDO_LIST = FUNERAL_SIDO_LIST.map((sido) => ({
  key: sido[1] as string,
  value: sido[0],
}));

const 기존장지 = [
  {
    text: "매장(묘지)",
    value: "매장(묘지)",
  },
  {
    text: "평장묘(화장 후 묘지로 모시는 형태)",
    value: "평장묘",
  },
  {
    text: "납골당",
    value: "납골당",
  },
  {
    text: "기타",
    value: "기타",
  },
];

export default function MatchFacilityRequestQuotation({
  defaultFacility,
}: {
  defaultFacility: any;
}) {
  const [selectFuneralSigungu, setSelectFuneralSigungu] = useState(true);
  const [selectFuneralSido, setSelectFuneralSido] = useState(false);

  const { onOpen, onClose } = useDialog();

  const [SelectedOption, setSelectedOption] = useReducer(
    (prev: FacilityMatchRequestProps, next: any) => {
      return { ...prev, ...next };
    },
    {
      count: "",
      is_open_ready: "",
      funeral_sido: "",
      funeral_sigungu: "",
      장지유형: "",
      facility_type: "",
      compare_need: "",
      phone: "",
      agreed: "",
    },
  );

  const isDisabled = useMemo(() => {
    return !SelectedOption.phone || !SelectedOption.agreed;
  }, [SelectedOption]);

  useEffect(() => {
    if (SelectedOption.funeral_sido) {
      setSelectFuneralSido(true);
      setSelectFuneralSigungu(false);
    }

    if (SelectedOption.funeral_sigungu) {
      setSelectFuneralSigungu(true);
    }
  }, [SelectedOption]);

  const handleSubmit = useCallback(async () => {
    if (loading) return;

    if (isDisabled) {
      onOpen({
        type: 1,
        closeOnOverlayClick: false,
        header: "응답하지 않은 항목이 있어요",
        children: <>다시 확인해주세요.</>,
        footerProps: {
          text: "확인",
          onClick: onClose,
        },
      });
      return;
    }

    try {
      const 장지시군구 = `${FUNERAL_SIDO.get(Number(defaultFacility.sido)) as string} ${
        FUNERAL_SIGUNGU.get(Number(defaultFacility.sido))?.get(
          Number(defaultFacility.sigungu),
        ) as string
      }`;
      const 계신지역 = `${FUNERAL_SIDO.get(Number(SelectedOption.funeral_sido)) as string} ${
        FUNERAL_SIGUNGU.get(Number(SelectedOption.funeral_sido))?.get(
          Number(SelectedOption.funeral_sigungu),
        ) as string
      }`;
      const value = SelectedOption.phone;
      const question = `
    *개장, 이장 문의 도착!*
    \n\n
      ${defaultFacility.companyname} 개장 이장 문의\n
      (${장지시군구}, *${defaultFacility.is_public ? "공설" : "사설"})
      
      (장례지도사에게 전달 시 추가 메세지와 함께 절취선 하단을 복사해주세요)
      -----
      *${장지시군구}에 위치한 ${defaultFacility.companyname}로 이장하시고자 문의주신 고객님입니다.*

      *[기본정보]*

      옮기고자 하시는 인원: ${SelectedOption.count}\n개장 공사 준비: ${
        SelectedOption.is_open_ready ? "이미 준비 하심" : "아직 준비하지 않음"
      }\n${defaultFacility.companyname} 이외 장지 비교 요청: ${SelectedOption.compare_need}\n
      ${
        SelectedOption.is_open_ready === "아니오" &&
        `*[현재 모시고 계신 곳]*

        지역: ${계신지역}\n
      모시고 계신 상태: ${SelectedOption.장지유형}\n
      모시고 계시는 시설 종류: ${SelectedOption.facility_type}
      -----
      `
      }`;
      if (/^010-\d{4}-\d{4}$/.test(value)) {
        emailjs
          .send(
            "service_agc6alu",
            "template_o1ul2fg",
            {
              question,
              phone: value,
            },
            "ckK66U9O1M16QnFJq",
          )
          .then(() => {
            alert("제출이 완료되었습니다.");
            onClose();
            setLoading(false);
            customEvent({
              newGtm: {
                path: "facility_relocate",
                trigger_and_target: "submit_success",
              },
            });
          })
          .finally(() => setLoading(false));
      } else if (/^010\d{4}\d{4}$/.test(value)) {
        emailjs
          .send(
            "service_agc6alu",
            "template_o1ul2fg",
            {
              question,
              phone: `010-${value.substring(3, 7)}-${value.substring(7, 11)}`,
            },
            "ckK66U9O1M16QnFJq",
          )
          .then(() => {
            alert("제출이 완료되었습니다.");
            onClose();
            setLoading(false);
            customEvent({
              newGtm: {
                path: "facility_relocate",
                trigger_and_target: "submit_success",
              },
            });
          })
          .finally(() => setLoading(false));
      } else {
        alert("번호 형식을 확인해주세요. (010-****-****)");
        return;
      }
      setLoading(true);
    } catch (e: any) {
    } finally {
      setLoading(false);
    }
  }, [SelectedOption]);

  const [loading, setLoading] = useRecoilState(matchRequestFacilityLoadingState);

  return (
    <>
      <VStack padding="24px" width="100%">
        <VStack gap="16px" position="relative">
          <Box className="subtitle2_sb gray900">몇 분을 다른 곳으로 모시고자 하시나요</Box>
          <Field.ButtonGroups
            items={["1~2위", "3위 이상"]}
            value={SelectedOption.count}
            buttonWidth="calc(50% - 4px)"
            onChange={(v) => {
              setSelectedOption({ count: v });
            }}
          />
          <Spacing gap={12} />

          <Box className="subtitle2_sb gray900">
            개장 작업(공사, 인력 등)은 준비하신 상태인가요?
          </Box>
          <Field.ButtonGroups
            items={["예", "아니오"]}
            value={SelectedOption.is_open_ready}
            buttonWidth="calc(50% - 4px)"
            onChange={(v) => {
              setSelectedOption({ is_open_ready: v });
            }}
          />
          <Spacing gap={12} />
          {SelectedOption.is_open_ready && (
            <>
              {SelectedOption.is_open_ready === "아니오" && (
                <VStack>
                  <Box className="subtitle2_sb gray900">현재 모시고 계신 지역은 어디신가요?</Box>
                  <Spacing gap={24} />

                  <HStack gap="12px">
                    <Dropdown
                      name="지역"
                      forceZIndex={100}
                      value={SelectedOption.funeral_sido}
                      values={SIDO_LIST}
                      onChange={(v) => {
                        setSelectedOption({ funeral_sido: v, funeral_sigungu: undefined });
                      }}
                    />
                    <Dropdown
                      name="지역"
                      forceZIndex={100}
                      value={SelectedOption.funeral_sigungu}
                      values={
                        SelectedOption.funeral_sido
                          ? Array.from(FUNERAL_SIGUNGU.get(SelectedOption.funeral_sido)!).map(
                              (sigungu) => ({
                                key: sigungu[1],
                                value: sigungu[0],
                              }),
                            )
                          : []
                      }
                      onChange={(v) => {
                        setSelectedOption({ funeral_sigungu: v });
                      }}
                    />
                  </HStack>
                  {selectFuneralSigungu || (
                    <div className="orange650 button">다음 지역을 선택해주세요.</div>
                  )}
                  {selectFuneralSido || (
                    <div className="orange650 button">지역을 선택해주세요.</div>
                  )}
                  <Spacing gap={48} />

                  <Field.Radio
                    title="모시고 계시는 장지 유형를 알려주세요"
                    items={기존장지.map((item) => item.text)}
                    value={SelectedOption.장지유형}
                    onChange={(e) => {
                      setSelectedOption({ 장지유형: e });
                    }}
                    radioWidth="100%"
                    flexDirection="column"
                  />
                  <Spacing gap={40} />

                  <Field.Radio
                    title="현재 모시고 있는 장지 시설 종류를 알려주세요"
                    items={[
                      "개인 선산(사유지)에 모시고 있어요",
                      "공설 시설(시립, 도립 등)에 모시고 있어요",
                      "사설 시설(묘원, 공원 등)에 모시고 있어요",
                    ]}
                    value={SelectedOption.facility_type}
                    onChange={(e) => {
                      setSelectedOption({ facility_type: e });
                    }}
                    radioWidth="100%"
                    flexDirection="column"
                  />
                  <Spacing gap={40} />
                </VStack>
              )}
              <>
                <Box className="subtitle2_sb gray900">
                  {`${defaultFacility.companyname} 이외의 다른 장지까지 함께 비교하고자 하시나요?`}
                </Box>
                <Field.ButtonGroups
                  items={["예", "아니오"]}
                  value={SelectedOption.compare_need}
                  buttonWidth="calc(50% - 4px)"
                  onChange={(v) => {
                    setSelectedOption({ compare_need: v });
                  }}
                />
                <Spacing gap={40} />

                {SelectedOption.compare_need && (
                  <VStack gap={20}>
                    <Box className="subtitle2_sb gray900">
                      남겨주신 번호로 필요 절차, 비용을 안내드립니다.
                    </Box>
                    <Input
                      value={SelectedOption.phone}
                      onChange={(e) => setSelectedOption({ phone: e.target.value })}
                      border={`1px solid ${colors.gray400}`}
                      width="100%"
                      placeholder={"010-****-****"}
                    />
                    <HStack alignItems="center" justifyContent="space-between">
                      <Checkbox
                        index={2}
                        label="개인정보 수집, 이용, 제공 동의"
                        checked={SelectedOption.agreed}
                        onChange={(e) => setSelectedOption({ agreed: e })}
                      />
                      <a
                        className="caption_11_b"
                        href="https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d"
                        target="_blank"
                      >
                        보기
                      </a>
                    </HStack>
                  </VStack>
                )}
              </>
            </>
          )}
        </VStack>
        {/* 
        <Spacing gap={60} />
        {isVariationB && (
          <>
            <Field.Radio
              title="희망하시는 장지 유형이 있으신가요?"
              items={장지유형_LIST.map((item) => item.text)}
              value={SelectedOption.장지유형}
              onChange={(e) => {
                setSelectedOption({ 장지유형: e });
                customEvent({
                  newGtm: {
                    path: eventPath,
                    trigger_and_target: "jangji_type-answer",
                    text: String(e),
                  },
                });
              }}
              radioWidth="100%"
              flexDirection="column"
            />
            <Spacing gap={40} />
          </>
        )}

        <Field.Radio
          title="가장 중요하게 생각하시는 부분이 무엇인가요?"
          items={isVariationB ? 장지핵심요소_LIST : 장지견적_장지핵심요소_LIST}
          value={SelectedOption.핵심요소}
          onChange={(e) => {
            setSelectedOption({ 핵심요소: e });
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "jangji_important_factor-answer",
                text: String(e),
              },
            });
          }}
          radioWidth="100%"
          flexDirection="column"
        />
        <Spacing gap={40} />

        <Field.Radio
          title="장지 준비가 급박한 상황인가요?"
          items={장지준비긴급도_LIST.map((item) => item.text)}
          value={SelectedOption.장지준비긴급도}
          onChange={(e) => {
            setSelectedOption({ 장지준비긴급도: e });
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "jangji_status-answer",
                text: String(e),
              },
            });
          }}
          radioWidth="100%"
          flexDirection="column"
        />

        <Spacing gap={40} />

        <Field.Radio
          title="희망하시는 추가 서비스가 있으신가요?"
          items={장지희망서비스_LIST.map((item) => item.text)}
          value={SelectedOption.희망서비스}
          onChange={(e) => {
            setSelectedOption({ 희망서비스: e });
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "jangji_desired_service-answer",
                text: String(e),
              },
            });
          }}
          radioWidth="100%"
          flexDirection="column"
        /> */}

        <Spacing gap={200} />
      </VStack>

      <FixedButton
        isFixed={true}
        disabled={isDisabled}
        handleSubmit={handleSubmit}
        loading={loading}
      />
    </>
  );
}

function FixedButton({
  handleSubmit,
  loading,
  disabled,
  isFixed = true,
  borderRadius = "0 0 10px 10px",
}: {
  handleSubmit: () => void;
  loading: boolean;
  disabled: boolean;
  isFixed?: boolean;
  borderRadius?: string;
}) {
  const { isBiggerThanMd } = useResponsive();
  return (
    <Box
      position={isFixed ? "fixed" : "relative"}
      bottom="0"
      width={isBiggerThanMd ? "480px" : "100%"}
      maxWidth={isBiggerThanMd ? "var(--global-max-width)" : "480px"}
      mt={isFixed ? "110px" : "24px"}
    >
      <LargeButton
        buttonType={disabled ? LARGEL_BUTTON_TYPE.DISABLED : LARGEL_BUTTON_TYPE.PRIMARY}
        disabled={disabled}
        borderRadius={borderRadius}
        height={60}
        onClick={handleSubmit}
      >
        <Box display="flex" maxWidth="300px">
          {loading ? <Spinner /> : "절차 비용 알아보기"}
        </Box>
      </LargeButton>
    </Box>
  );
}
